import React, { useState, useEffect, useRef } from 'react';
import CustomDropdown from '../../../UI/CustomDropdown';
import { useNotification } from '../../../../contexts/NotificationContext';
import apiService from '../../../../services/api';
import sidePanelStyles from '../../../../styles/SidePanel.module.css';
import buttonStyles from '../../../../styles/Buttons.module.css';
import customDropdownStyles from '../../../../styles/CustomDropdown.module.css';
import styles from '../../../../styles/TaskManagementTab.module.css';
import taskFormStyles from '../../../../styles/TaskForm.module.css';

export interface TaskFormData {
  name: string;
  description: string;
  category_id: string;
  priority: string;
  assignee_ids: string[];
  due_date: string;
  estimated_hours: number;
  approval_type: 'none' | 'single' | 'multiple';
  approver_ids?: string[]; // For multiple approvals: list of user IDs who need to approve
  approvers?: Array<{ id: string; user: any }>; // Full approver details (for edit mode)
  requires_documents: boolean;
  enable_group_chat: boolean;
  distribution_method: 'equal' | 'percentage' | 'custom' | 'direct';
  direct_assignment_type?: 'instances' | 'single'; // For direct assignment: create separate instances or combine as single task
  base_points: number;
  team_lead_points: number;
  point_allocation: Record<string, number>;
  work_descriptions: Record<string, string>;
  status?: string;
  // Recurrence/Scheduler fields
  is_recurring: boolean;
  recurrence_type: 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly';
  recurrence_weekly_days: number[]; // 0=Sunday, 1=Monday, etc.
  recurrence_monthly_dates: number[]; // [1, 15] means 1st and 15th of month
  recurrence_yearly_date: { month: number; day: number }; // { month: 1, day: 1 } = Jan 1
  recurrence_end_date?: string; // Optional end date for recurrence
  // User instance creation feature
  allow_user_instances: boolean;
  instance_template_name?: string;
  requires_team_lead_approval: boolean;
}

interface TaskFormProps {
  groupId: string;
  taskCategories: any[];
  groupMembers: any[];
  isLoadingCategories: boolean;
  onSubmit: (formData: TaskFormData, files: File[], documentMetadata: Record<string, any>) => Promise<void>;
  onCancel: () => void;
  initialData?: Partial<TaskFormData>;
  mode?: 'create' | 'edit';
  existingDocuments?: any[];
  onDeleteDocument?: (documentId: string) => void;
  onViewDocument?: (doc: any) => void;
  onDownloadDocument?: (doc: any) => void;
  showCategoryFilter?: boolean;
  categoryFilter?: 'all' | 'global' | 'group';
  onCategoryFilterChange?: (filter: 'all' | 'global' | 'group') => void;
  hierarchyMode?: boolean; // If true, working with hierarchy-based users instead of group members
  onAssigneeChange?: (assigneeIds: string[]) => void; // Callback when assignees change (for hierarchy mode)
  assigneesData?: any[]; // Full assignee objects from task details (for edit mode when groupMembers might be empty)
}

const TaskForm: React.FC<TaskFormProps> = ({
  groupId,
  taskCategories,
  groupMembers,
  isLoadingCategories,
  onSubmit,
  onCancel,
  initialData,
  mode = 'create',
  existingDocuments = [],
  onDeleteDocument,
  onViewDocument,
  onDownloadDocument,
  showCategoryFilter = false,
  categoryFilter = 'all',
  onCategoryFilterChange,
  hierarchyMode = false,
  onAssigneeChange,
  assigneesData = [],
}) => {
  const notificationService = useNotification();

  const initialApprovalType = initialData?.approval_type || ((initialData as any)?.requires_approval ? 'single' : 'none');

  const [formData, setFormData] = useState<TaskFormData>({
    name: initialData?.name || '',
    description: initialData?.description || '',
    category_id: initialData?.category_id || '',
    priority: initialData?.priority || 'medium',
    assignee_ids: initialData?.assignee_ids || [],
    due_date: initialData?.due_date || '',
    estimated_hours: initialData?.estimated_hours || 0,
    approval_type: initialApprovalType,
    approver_ids: initialData?.approver_ids || [],
    requires_documents: initialData?.requires_documents || false,
    enable_group_chat: initialData?.enable_group_chat || false,
    distribution_method: initialData?.distribution_method || 'equal',
    direct_assignment_type: initialData?.direct_assignment_type || 'single',
    base_points: initialData?.base_points || 0,
    team_lead_points: initialData?.team_lead_points || 0,
    point_allocation: initialData?.point_allocation || {},
    work_descriptions: initialData?.work_descriptions || {},
    status: initialData?.status || 'pending',
    is_recurring: initialData?.is_recurring || false,
    recurrence_type: initialData?.recurrence_type || 'none',
    recurrence_weekly_days: initialData?.recurrence_weekly_days || [],
    recurrence_monthly_dates: initialData?.recurrence_monthly_dates || [1],
    recurrence_yearly_date: initialData?.recurrence_yearly_date || { month: 1, day: 1 },
    recurrence_end_date: initialData?.recurrence_end_date || '',
    allow_user_instances: initialData?.allow_user_instances || false,
    instance_template_name: initialData?.instance_template_name || '',
    requires_team_lead_approval: initialApprovalType === 'single' ? true : (initialData?.requires_team_lead_approval || false),
  });

  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [documentMetadata, setDocumentMetadata] = useState<Record<string, {
    title: string;
    description: string;
    document_type: string;
    is_required: boolean;
  }>>({});
  const [isValidatingFiles, setIsValidatingFiles] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;

  // Approver search state (for multiple approvals)
  const [allApprovers, setAllApprovers] = useState<any[]>([]); // All available users
  const [approverSearchQuery, setApproverSearchQuery] = useState('');
  const [approverDropdownOpen, setApproverDropdownOpen] = useState(false);
  const [approverSearchLoading, setApproverSearchLoading] = useState(false);
  const [selectedApproversData, setSelectedApproversData] = useState<Record<string, any>>({});
  const approverDropdownRef = useRef<HTMLDivElement>(null);
  const approverSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Local state for fetching assignees if groupMembers is empty (global task fallback)
  const [localAssignees, setLocalAssignees] = useState<any[]>([]);

  // Load hierarchy users if global task and no members passed
  useEffect(() => {
    const loadGlobalAssignees = async () => {
      // Only fetch if global task (no groupId) and no members provided
      if (!groupId && groupMembers.length === 0) {
        try {
          const response = await apiService.getHierarchyAssignableUsers();
          if (response.status === 'success') {
            const users = response.data?.assignable_users || [];
            // Map to match groupMember structure
            const formattedUsers = users.map((user: any) => ({
              id: user.id,
              user: {
                id: user.id,
                first_name: user.first_name,
                last_name: user.last_name,
                email: user.email,
                get_full_name: user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim()
              },
              groups: user.groups || []
            }));
            setLocalAssignees(formattedUsers);
          }
        } catch (error: any) {
          console.error('Error loading global assignees fallback:', error);
          notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to load global assignees');
        }
      }
    };

    loadGlobalAssignees();
  }, [groupId, groupMembers.length]);

  const priorityOptions = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' }
  ];

  // Recurrence/Scheduler fields
  const [newPreApprovalDocument, setNewPreApprovalDocument] = useState('');

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  // Load approver details when editing
  useEffect(() => {
    const loadUsersByIds = async (ids: string[], targetMap: Record<string, any>) => {
      const loadingPromises = ids.map(async (id: string) => {
        const idStr = String(id);
        if (targetMap[idStr]) return; // Already loaded

        try {
          const response = await apiService.getUserDetails(idStr);
          if (response.status === 'success' && response.data) {
            targetMap[idStr] = response.data;
          }
        } catch (error: any) {
          console.error(`Error loading user ${idStr}:`, error);
          // Fallback search
          try {
            const searchResponse = await apiService.searchUsers(idStr);
            if (searchResponse.status === 'success' && searchResponse.data) {
              const foundUser = searchResponse.data.find((user: any) => String(user.id) === idStr);
              if (foundUser) {
                targetMap[idStr] = foundUser;
              }
            }
          } catch (searchError: any) {
            console.error(`Error searching for user ${idStr}:`, searchError);
          }
        }
      });
      await Promise.all(loadingPromises);
    };

    const loadApproverData = async () => {
      if (mode === 'edit' && initialData) {
        const approversMap: Record<string, any> = {};

        // First, try to use approvers array if available
        if (initialData.approvers && Array.isArray(initialData.approvers) && initialData.approvers.length > 0) {
          initialData.approvers.forEach((approver: any) => {
            const userId = approver.user?.id || approver.id;
            if (userId) {
              // Normalize ID to string for consistent key matching
              const userIdStr = String(userId);
              approversMap[userIdStr] = approver.user || approver;
            }
          });
        }

        // Logic for regular approvers (Task Completion)
        if (initialData.approver_ids && Array.isArray(initialData.approver_ids) && initialData.approver_ids.length > 0) {
          const missingApproverIds = initialData.approver_ids.filter((id: string) => {
            const idStr = String(id);
            return !approversMap[idStr];
          });

          if (missingApproverIds.length > 0) {
            await loadUsersByIds(missingApproverIds, approversMap);
          }
        }

        if (Object.keys(approversMap).length > 0) {
          setSelectedApproversData(approversMap);
        }

        if (Object.keys(approversMap).length > 0) {
          setSelectedApproversData(approversMap);
        }
      }
    };

    loadApproverData();
  }, [mode, initialData?.approvers, initialData?.approver_ids]);

  // Update formData when initialData changes (for edit mode)
  useEffect(() => {
    if (mode === 'edit' && initialData) {
      // Normalize approver_ids to strings for consistency
      const approverIds = initialData.approver_ids
        ? (Array.isArray(initialData.approver_ids) ? initialData.approver_ids.map(id => String(id)) : [])
        : [];

      // Normalize assignee_ids to strings for consistent comparison
      const assigneeIds = initialData.assignee_ids
        ? (Array.isArray(initialData.assignee_ids) ? initialData.assignee_ids.map(id => String(id)) : [])
        : [];

      // Normalize work_descriptions keys to strings
      const normalizedWorkDescriptions: Record<string, string> = {};
      if (initialData.work_descriptions && typeof initialData.work_descriptions === 'object') {
        Object.keys(initialData.work_descriptions).forEach(key => {
          const value = initialData.work_descriptions?.[key];
          if (value) {
            normalizedWorkDescriptions[String(key)] = value;
          }
        });
      }

      setFormData(prev => ({
        ...prev,
        name: initialData.name ?? prev.name,
        description: initialData.description ?? prev.description,
        category_id: initialData.category_id ?? prev.category_id,
        priority: initialData.priority ?? prev.priority,
        assignee_ids: assigneeIds.length > 0 ? assigneeIds : (prev.assignee_ids || []),
        due_date: initialData.due_date ?? prev.due_date,
        estimated_hours: initialData.estimated_hours ?? prev.estimated_hours,
        status: initialData.status ?? prev.status,
        approval_type: initialData.approval_type ?? prev.approval_type,
        approver_ids: approverIds.length > 0 ? approverIds : (prev.approver_ids || []),
        requires_documents: initialData.requires_documents ?? prev.requires_documents,
        enable_group_chat: initialData.enable_group_chat ?? prev.enable_group_chat,
        distribution_method: initialData.distribution_method ?? prev.distribution_method,
        direct_assignment_type: initialData.direct_assignment_type ?? prev.direct_assignment_type,
        base_points: initialData.base_points ?? prev.base_points,
        team_lead_points: initialData.team_lead_points ?? prev.team_lead_points,
        point_allocation: initialData.point_allocation ?? prev.point_allocation,
        work_descriptions: Object.keys(normalizedWorkDescriptions).length > 0 ? normalizedWorkDescriptions : (prev.work_descriptions || {}),
        is_recurring: initialData.is_recurring ?? prev.is_recurring,
        recurrence_type: initialData.recurrence_type ?? prev.recurrence_type,
        recurrence_weekly_days: initialData.recurrence_weekly_days ?? prev.recurrence_weekly_days,
        recurrence_monthly_dates: initialData.recurrence_monthly_dates ?? prev.recurrence_monthly_dates,
        recurrence_yearly_date: initialData.recurrence_yearly_date ?? prev.recurrence_yearly_date,
        recurrence_end_date: initialData.recurrence_end_date ?? prev.recurrence_end_date,
        allow_user_instances: initialData.allow_user_instances ?? prev.allow_user_instances,
        instance_template_name: initialData.instance_template_name ?? prev.instance_template_name,
      }));
    }
  }, [mode, initialData]);

  useEffect(() => {
    if (formData.category_id && mode === 'create') {
      // Only auto-update base_points and team_lead_points in create mode
      // In edit mode, preserve the existing values from initialData
      const selectedCategory = taskCategories.find(cat => cat.id === formData.category_id);
      if (selectedCategory) {
        const basePoints = selectedCategory.scheme_configuration?.base_points || selectedCategory.default_points || 0;
        const teamLeadConfig = selectedCategory.scheme_configuration?.team_lead_points;
        let teamLeadPoints = 0;

        if (teamLeadConfig) {
          if (teamLeadConfig.type === 'percentage') {
            teamLeadPoints = Math.round((basePoints * teamLeadConfig.percentage) / 100);
          } else if (teamLeadConfig.type === 'fixed') {
            teamLeadPoints = teamLeadConfig.fixed_points || 0;
          }
        }

        setFormData(prev => ({
          ...prev,
          base_points: basePoints,
          team_lead_points: teamLeadPoints,
        }));
      }
    }
  }, [formData.category_id, taskCategories, mode]);

  // Trigger callback when assignees change via initialData (for hierarchy mode)
  useEffect(() => {
    if (hierarchyMode && onAssigneeChange && initialData?.assignee_ids) {
      onAssigneeChange(initialData.assignee_ids);
    }
  }, [hierarchyMode, onAssigneeChange, initialData?.assignee_ids]);

  const addAssignee = (assigneeId: string) => {
    if (!formData.assignee_ids.includes(assigneeId)) {
      const newAssigneeIds = [...formData.assignee_ids, assigneeId];
      setFormData(prev => ({
        ...prev,
        assignee_ids: newAssigneeIds
      }));
      // Notify parent when assignees change (for hierarchy mode group determination)
      if (hierarchyMode && onAssigneeChange) {
        onAssigneeChange(newAssigneeIds);
      }
    }
  };

  const removeAssignee = (assigneeId: string) => {
    const newAssigneeIds = formData.assignee_ids.filter(id => id !== assigneeId);
    setFormData(prev => ({
      ...prev,
      assignee_ids: newAssigneeIds,
      work_descriptions: Object.fromEntries(
        Object.entries(prev.work_descriptions).filter(([key]) => key !== assigneeId)
      ),
      point_allocation: Object.fromEntries(
        Object.entries(prev.point_allocation).filter(([key]) => key !== assigneeId)
      ),
    }));
    // Notify parent when assignees change (for hierarchy mode group determination)
    if (hierarchyMode && onAssigneeChange) {
      onAssigneeChange(newAssigneeIds);
    }
  };

  const selectAllAssignees = () => {
    const sourceMembers = groupMembers.length > 0 ? groupMembers : localAssignees;
    const allMemberIds = sourceMembers.map(member => member.user?.id || member.id);
    setFormData(prev => ({
      ...prev,
      assignee_ids: allMemberIds,
    }));
    // Notify parent when assignees change (for hierarchy mode group determination)
    if (hierarchyMode && onAssigneeChange) {
      onAssigneeChange(allMemberIds);
    }
  };

  const clearAllAssignees = () => {
    setFormData(prev => ({
      ...prev,
      assignee_ids: [],
      work_descriptions: {},
      point_allocation: {},
    }));
    // Notify parent when assignees change (for hierarchy mode group determination)
    if (hierarchyMode && onAssigneeChange) {
      onAssigneeChange([]);
    }
  };

  const getSelectedAssignees = () => {
    const assigneeIdsStr = formData.assignee_ids.map(id => String(id));

    const selectedFromGroup = groupMembers.filter(member => {
      const memberId = member.user?.id || member.id;
      const memberIdStr = memberId ? String(memberId) : null;
      return memberIdStr && assigneeIdsStr.includes(memberIdStr);
    });

    const foundIds = new Set(selectedFromGroup.map(m => String(m.user?.id || m.id)));

    let selectedFromData: any[] = [];
    if (assigneesData.length > 0 && mode === 'edit') {
      selectedFromData = assigneesData
        .filter((assignee: any) => {
          const userId = String(assignee.user?.id || assignee.id);
          return assigneeIdsStr.includes(userId) && !foundIds.has(userId);
        })
        .map((assignee: any) => ({
          id: assignee.id || assignee.user?.id,
          user: {
            id: assignee.user?.id,
            first_name: assignee.user?.first_name,
            last_name: assignee.user?.last_name,
            email: assignee.user?.email,
            get_full_name: assignee.user?.full_name || `${assignee.user?.first_name || ''} ${assignee.user?.last_name || ''}`.trim()
          },
          groups: []
        }));
    }

    const selected = [...selectedFromGroup, ...selectedFromData];

    // Debug logging for edit mode
    if (mode === 'edit') {
      console.log('getSelectedAssignees result:', {
        formDataAssigneeIds: formData.assignee_ids,
        assigneeIdsStr,
        groupMembersCount: groupMembers.length,
        assigneesDataCount: assigneesData.length,
        selectedCount: selected.length,
        selected: selected.map(m => ({
          id: m.user?.id || m.id,
          name: `${m.user?.first_name || ''} ${m.user?.last_name || ''}`.trim()
        }))
      });
    }

    return selected;
  };

  const getAvailableAssignees = () => {
    // Normalize assignee_ids to strings for comparison
    const assigneeIdsStr = formData.assignee_ids.map(id => String(id));

    // Use groupMembers if available, otherwise fallback to localAssignees
    const sourceMembers = groupMembers.length > 0 ? groupMembers : localAssignees;

    // Return members that are not already selected
    return sourceMembers.filter(member => {
      const memberId = member.user?.id || member.id;
      const memberIdStr = memberId ? String(memberId) : null;
      return !memberIdStr || !assigneeIdsStr.includes(memberIdStr);
    });
  };

  // Approver management functions (for multiple approvals)
  const loadApprovers = async (query: string) => {
    // Load users from database based on search query
    setApproverSearchLoading(true);
    try {
      const searchQuery = query.trim();
      let response;

      if (searchQuery.length >= 2) {
        // Search with the provided query
        response = await apiService.searchUsers(searchQuery);
      } else if (searchQuery.length === 1) {
        // For single character or initial load, search with that character
        response = await apiService.searchUsers(searchQuery);
      } else {
        // For empty query on initial load, get some default results
        response = await apiService.searchUsers('a');
      }

      if (response.status === 'success') {
        const users = response.data || [];
        setAllApprovers(users);
      } else {
        setAllApprovers([]);
      }
    } catch (error: any) {
      console.error('Error loading approvers:', error);
      notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to load approvers');
      setAllApprovers([]);
    } finally {
      setApproverSearchLoading(false);
    }
  };

  // Filter approvers based on search query
  const getFilteredApprovers = () => {
    const selectedApproverIds = (formData.approver_ids || []).map(id => String(id));
    const query = approverSearchQuery.toLowerCase().trim();

    return allApprovers.filter((user: any) => {
      // Exclude already selected approvers (normalize IDs to strings for comparison)
      const userIdStr = String(user.id);
      if (selectedApproverIds.includes(userIdStr)) {
        return false;
      }

      // Filter by search query - if no query, show all
      if (!query) return true;

      const fullName = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase();
      const email = (user.email || '').toLowerCase();
      const designation = formatDesignation(user.designation).toLowerCase();
      const displayName = getUserDisplayName(user).toLowerCase();
      const userId = (user.id || '').toLowerCase();

      return fullName.includes(query) ||
        email.includes(query) ||
        designation.includes(query) ||
        displayName.includes(query) ||
        userId.includes(query);
    });
  };

  const addApprover = (user: any) => {
    const approverIds = (formData.approver_ids || []).map(id => String(id));
    const userIdStr = String(user.id);
    if (!approverIds.includes(userIdStr)) {
      setFormData(prev => ({
        ...prev,
        approver_ids: [...(prev.approver_ids || []), userIdStr]
      }));
      // Store user data for display (use string key)
      setSelectedApproversData(prev => ({
        ...prev,
        [userIdStr]: user
      }));
    }
    // Don't clear search query - keep dropdown open for selecting more approvers
  };

  const removeApprover = (approverId: string) => {
    const approverIdStr = String(approverId);
    setFormData(prev => ({
      ...prev,
      approver_ids: (prev.approver_ids || []).filter(id => String(id) !== approverIdStr)
    }));
    // Remove from stored data
    setSelectedApproversData(prev => {
      const newData = { ...prev };
      delete newData[approverIdStr];
      return newData;
    });
  };

  // Load initial users when dropdown opens
  useEffect(() => {
    if (approverDropdownOpen && allApprovers.length === 0) {
      // Load initial batch of users when dropdown first opens
      loadApprovers('a'); // Load users starting with 'a' as initial set
    }
  }, [approverDropdownOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear search when dropdown closes
  useEffect(() => {
    if (!approverDropdownOpen) {
      setApproverSearchQuery('');
    }
  }, [approverDropdownOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (approverDropdownRef.current && !approverDropdownRef.current.contains(event.target as Node)) {
        setApproverDropdownOpen(false);
      }
    };

    if (approverDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [approverDropdownOpen]);

  const formatDesignation = (designation: string | null | undefined): string => {
    if (!designation) return '';
    // Convert snake_case to Title Case
    return designation.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  const getUserDisplayName = (user: any): string => {
    const name = user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim();
    const designation = formatDesignation(user.designation);
    return designation ? `${name} (${designation})` : name;
  };

  const getBasePoints = () => {
    const selectedCategory = taskCategories.find(cat => cat.id === formData.category_id);
    if (!selectedCategory) return 0;
    return selectedCategory.scheme_configuration?.base_points || selectedCategory.default_points || 0;
  };

  const getTeamLeadPoints = () => {
    const selectedCategory = taskCategories.find(cat => cat.id === formData.category_id);
    if (!selectedCategory) return 0;

    const teamLeadConfig = selectedCategory.scheme_configuration?.team_lead_points;
    if (!teamLeadConfig) return 0;

    if (teamLeadConfig.type === 'percentage') {
      const basePoints = getBasePoints();
      return Math.round((basePoints * teamLeadConfig.percentage) / 100);
    } else if (teamLeadConfig.type === 'fixed') {
      return teamLeadConfig.fixed_points || 0;
    }

    return 0;
  };

  const getRemainingPoints = () => {
    const basePoints = getBasePoints();
    const teamLeadPoints = getTeamLeadPoints();
    return basePoints - teamLeadPoints;
  };

  const getTotalAllocatedPoints = () => {
    if (formData.distribution_method === 'direct') {
      // Direct assignment: each person gets full base points
      return formData.assignee_ids.length * getBasePoints();
    } else if (formData.distribution_method === 'percentage') {
      return Object.values(formData.point_allocation).reduce((sum, val) => sum + (val || 0), 0);
    } else if (formData.distribution_method === 'custom') {
      return Object.values(formData.point_allocation).reduce((sum, val) => sum + (val || 0), 0);
    }
    return 0;
  };

  const isPointAllocationValid = () => {
    if (formData.distribution_method === 'direct') {
      // Direct assignment is always valid - each person gets full base points
      return true;
    } else if (formData.distribution_method === 'percentage') {
      const totalPercentage = getTotalAllocatedPoints();
      return Math.abs(totalPercentage - 100) < 0.01;
    } else if (formData.distribution_method === 'custom') {
      const totalPoints = getTotalAllocatedPoints();
      const remainingPoints = getRemainingPoints();
      return totalPoints <= remainingPoints;
    }
    return true;
  };

  const getPointAllocationMessage = () => {
    if (formData.distribution_method === 'direct') {
      const basePoints = getBasePoints();
      return { type: 'success', message: `Each assignee will receive ${basePoints} points directly (no splitting)` };
    } else if (formData.distribution_method === 'percentage') {
      const totalPercentage = getTotalAllocatedPoints();
      if (Math.abs(totalPercentage - 100) < 0.01) {
        return { type: 'success', message: 'Percentage allocation is valid (100%)' };
      } else {
        return { type: 'error', message: `Total percentage must equal 100%. Current: ${totalPercentage.toFixed(1)}%` };
      }
    } else if (formData.distribution_method === 'custom') {
      const totalPoints = getTotalAllocatedPoints();
      const remainingPoints = getRemainingPoints();
      if (totalPoints <= remainingPoints) {
        return { type: 'success', message: `Point allocation is valid (${totalPoints}/${remainingPoints} points)` };
      } else {
        return { type: 'error', message: `Total points cannot exceed ${remainingPoints}. Current: ${totalPoints}` };
      }
    }
    return { type: 'info', message: 'Equal distribution - no additional configuration needed' };
  };

  const updatePointAllocation = (assigneeId: string, value: number) => {
    setFormData(prev => ({
      ...prev,
      point_allocation: {
        ...prev.point_allocation,
        [assigneeId]: value
      }
    }));
  };

  // File validation
  const validateFileContent = async (file: File): Promise<boolean> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const arrayBuffer = e.target?.result as ArrayBuffer;
        const uint8Array = new Uint8Array(arrayBuffer);

        const executableSignatures = [
          [0x4D, 0x5A], // MZ
          [0x7F, 0x45, 0x4C, 0x46], // ELF
          [0xFE, 0xED, 0xFA, 0xCE], [0xFE, 0xED, 0xFA, 0xCF],
          [0xCE, 0xFA, 0xED, 0xFE], [0xCF, 0xFA, 0xED, 0xFE],
          [0xCA, 0xFE, 0xBA, 0xBE], // Java class
        ];

        const fileName = file.name.toLowerCase();
        const suspiciousZipExtensions = ['.jar', '.war', '.ear', '.exe'];
        const isSuspiciousZipFile = suspiciousZipExtensions.some(ext => fileName.endsWith(ext));

        if (isSuspiciousZipFile) {
          const zipSignatures = [
            [0x50, 0x4B, 0x03, 0x04], [0x50, 0x4B, 0x05, 0x06], [0x50, 0x4B, 0x07, 0x08],
          ];
          executableSignatures.push(...zipSignatures);
        }

        for (const signature of executableSignatures) {
          if (signature.every((byte, index) => uint8Array[index] === byte)) {
            resolve(false);
            return;
          }
        }

        resolve(true);
      };
      reader.readAsArrayBuffer(file.slice(0, 512));
    });
  };

  const processFiles = async (files: File[]) => {
    setIsValidatingFiles(true);
    const validFiles: File[] = [];
    const rejectedFiles: string[] = [];

    try {
      for (const file of files) {
        const dangerousExtensions = [
          '.exe', '.bat', '.cmd', '.com', '.pif', '.scr', '.vbs', '.js', '.jar',
          '.app', '.deb', '.rpm', '.msi', '.dmg', '.pkg', '.sh', '.ps1', '.py',
          '.php', '.asp', '.jsp', '.pl', '.rb', '.go', '.c', '.cpp', '.h'
        ];

        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

        if (dangerousExtensions.includes(fileExtension)) {
          rejectedFiles.push(`${file.name} (executable/script file)`);
          continue;
        }

        if (file.size > 50 * 1024 * 1024) {
          rejectedFiles.push(`${file.name} (file too large: ${(file.size / 1024 / 1024).toFixed(1)}MB)`);
          continue;
        }

        const dangerousMimeTypes = [
          'application/x-executable', 'application/x-msdownload', 'application/x-msdos-program',
          'application/x-winexe', 'application/x-msi', 'application/x-debian-package',
          'application/x-rpm', 'application/x-apple-diskimage', 'application/x-sh',
          'application/x-powershell', 'text/x-script', 'application/x-php',
          'application/x-httpd-php', 'application/x-javascript', 'application/javascript'
        ];

        if (dangerousMimeTypes.some(type => file.type.toLowerCase().includes(type))) {
          rejectedFiles.push(`${file.name} (dangerous file type)`);
          continue;
        }

        try {
          const isContentSafe = await validateFileContent(file);
          if (!isContentSafe) {
            rejectedFiles.push(`${file.name} (contains executable content)`);
            continue;
          }
        } catch (error: any) {
          console.warn(`Could not validate content for ${file.name}:`, error);
          rejectedFiles.push(`${file.name} (content validation failed)`);
          continue;
        }

        validFiles.push(file);
      }

      if (rejectedFiles.length > 0) {
        notificationService.error(`The following files were rejected for security reasons: ${rejectedFiles.join(', ')}`);
      }

      if (validFiles.length === 0) {
        return;
      }

      setUploadedFiles(prev => [...prev, ...validFiles]);

      const newMetadata = { ...documentMetadata };
      validFiles.forEach((file, index) => {
        const fileId = `${Date.now()}_${index}_${Math.random().toString(36).substr(2, 9)}`;
        newMetadata[fileId] = {
          title: file.name.replace(/\.[^/.]+$/, ""),
          description: '',
          document_type: 'attachment',
          is_required: false
        };
      });
      setDocumentMetadata(newMetadata);
    } finally {
      setIsValidatingFiles(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    await processFiles(files);
    event.target.value = '';
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.classList.add('dragover');
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.classList.remove('dragover');
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.classList.remove('dragover');
    const files = Array.from(event.dataTransfer.files);
    if (files.length > 0) {
      await processFiles(files);
    }
  };

  const removeFile = (fileIndex: number) => {
    setUploadedFiles(prev => prev.filter((_, index) => index !== fileIndex));
    const metadataKeys = Object.keys(documentMetadata);
    if (metadataKeys[fileIndex]) {
      const newMetadata = { ...documentMetadata };
      delete newMetadata[metadataKeys[fileIndex]];
      setDocumentMetadata(newMetadata);
    }
  };

  const updateDocumentMetadata = (fileIndex: number, field: string, value: string | boolean) => {
    const fileId = Object.keys(documentMetadata)[fileIndex];
    if (fileId) {
      setDocumentMetadata(prev => ({
        ...prev,
        [fileId]: {
          ...prev[fileId],
          [field]: value
        }
      }));
    }
  };

  const getFileIcon = (fileType: string, filename?: string) => {
    const lowerFileType = fileType.toLowerCase();
    const lowerFilename = filename?.toLowerCase() || '';

    // PDF
    if (lowerFileType.includes('pdf') || lowerFilename.endsWith('.pdf')) {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className={styles.fileIcon}>
          <rect x="3" y="4" width="18" height="18" rx="2" fill="#ef4444" />
          <path d="M8 8h8M8 12h8M8 16h5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
          <path d="M14 4v4h4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }

    // DOC / DOCX
    if (lowerFileType.includes('msword') || lowerFileType.includes('wordprocessingml') ||
      lowerFilename.endsWith('.doc') || lowerFilename.endsWith('.docx')) {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className={styles.fileIcon}>
          <rect x="3" y="4" width="18" height="18" rx="2" fill="#2563eb" />
          <path d="M8 8h8M8 12h8M8 16h5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
          <path d="M14 4v4h4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }

    // TXT
    if (lowerFileType.includes('text/plain') || lowerFilename.endsWith('.txt')) {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className={styles.fileIcon}>
          <rect x="3" y="4" width="18" height="18" rx="2" fill="#6b7280" />
          <path d="M8 8h8M8 12h8M8 16h5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
          <path d="M14 4v4h4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }

    // JPG / JPEG
    if (lowerFileType.includes('jpeg') || lowerFileType.includes('jpg') ||
      lowerFilename.endsWith('.jpg') || lowerFilename.endsWith('.jpeg')) {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className={styles.fileIcon}>
          <rect x="3" y="4" width="18" height="18" rx="2" fill="#16a34a" />
          <circle cx="9" cy="10" r="2" fill="white" opacity="0.8" />
          <path d="M15 16l-3-3-2 2-1-1-2 2" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M14 4v4h4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }

    // PNG
    if (lowerFileType.includes('png') || lowerFilename.endsWith('.png')) {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className={styles.fileIcon}>
          <rect x="3" y="4" width="18" height="18" rx="2" fill="#0891b2" />
          <circle cx="9" cy="10" r="2" fill="white" opacity="0.8" />
          <path d="M15 16l-3-3-2 2-1-1-2 2" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M14 4v4h4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }

    // XLS / XLSX
    if (lowerFileType.includes('excel') || lowerFileType.includes('spreadsheetml') ||
      lowerFilename.endsWith('.xls') || lowerFilename.endsWith('.xlsx')) {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className={styles.fileIcon}>
          <rect x="3" y="4" width="18" height="18" rx="2" fill="#16a34a" />
          <path d="M8 8h8M8 12h8M8 16h8" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
          <path d="M6 8v8M10 8v8M14 8v8" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
          <path d="M14 4v4h4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }

    // PPT / PPTX
    if (lowerFileType.includes('presentationml') || lowerFileType.includes('powerpoint') ||
      lowerFilename.endsWith('.ppt') || lowerFilename.endsWith('.pptx')) {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className={styles.fileIcon}>
          <rect x="3" y="4" width="18" height="18" rx="2" fill="#ea580c" />
          <rect x="7" y="9" width="10" height="8" rx="1" fill="white" opacity="0.2" />
          <path d="M9 12h6M9 15h4" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
          <path d="M14 4v4h4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }

    // GIF
    if (lowerFileType.includes('gif') || lowerFilename.endsWith('.gif')) {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className={styles.fileIcon}>
          <rect x="3" y="4" width="18" height="18" rx="2" fill="#8b5cf6" />
          <circle cx="9" cy="10" r="2" fill="white" opacity="0.8" />
          <path d="M15 16l-3-3-2 2-1-1-2 2" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M14 4v4h4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }

    // Default generic file icon
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className={styles.fileIcon}>
        <rect x="3" y="4" width="18" height="18" rx="2" fill="#6b7280" />
        <path d="M8 8h8M8 12h8M8 16h5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M14 4v4h4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  };




  // Step validation functions
  const validateStep1 = (): boolean => {
    if (!formData.name.trim()) {
      notificationService.error('Please enter a task name');
      return false;
    }
    if (!formData.category_id) {
      notificationService.error('Please select a point scheme');
      return false;
    }
    return true;
  };

  const validateStep2 = (): boolean => {
    if (formData.assignee_ids.length === 0) {
      notificationService.error(formData.allow_user_instances
        ? 'Please select at least one user who can create instances from this template'
        : 'Please select at least one assignee');
      return false;
    }
    if (!formData.allow_user_instances) {
      const missingWorkDescriptions = formData.assignee_ids.filter(assigneeId =>
        !formData.work_descriptions[assigneeId] || !formData.work_descriptions[assigneeId].trim()
      );
      if (missingWorkDescriptions.length > 0) {
        const assigneeNames = missingWorkDescriptions.map(id => {
          const member = groupMembers.find(m => (m.user?.id || m.id) === id);
          return member ? `${member.user?.first_name || ''} ${member.user?.last_name || ''}`.trim() || 'Unknown' : 'Unknown';
        });
        notificationService.error(`Please provide work descriptions for: ${assigneeNames.join(', ')}`);
        return false;
      }
    }
    if (formData.assignee_ids.length > 1 && (formData.distribution_method === 'percentage' || formData.distribution_method === 'custom') && !isPointAllocationValid()) {
      notificationService.error('Please fix the point allocation before continuing');
      return false;
    }
    return true;
  };

  const validateStep3 = (): boolean => {
    if (formData.approval_type === 'multiple') {
      if (!formData.approver_ids || formData.approver_ids.length === 0) {
        notificationService.error('Please select at least one approver for multiple approvals');
        return false;
      }
    }

    if (formData.is_recurring) {
      if (formData.recurrence_type === 'weekly' && (!formData.recurrence_weekly_days || formData.recurrence_weekly_days.length === 0)) {
        notificationService.error('Please select at least one day for weekly recurrence');
        return false;
      }
      if (formData.recurrence_type === 'monthly' && (!formData.recurrence_monthly_dates || formData.recurrence_monthly_dates.length === 0)) {
        notificationService.error('Please select at least one date for monthly recurrence');
        return false;
      }
    }
    return true;
  };

  const handleNext = (e?: React.MouseEvent<HTMLButtonElement>) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (currentStep === 1 && !validateStep1()) return;
    if (currentStep === 2 && !validateStep2()) return;
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = (e?: React.MouseEvent<HTMLButtonElement>) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Final validation
    if (!validateStep1() || !validateStep2() || !validateStep3()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData, uploadedFiles, documentMetadata);
    } catch (error: any) {
      console.error('Error submitting form:', error);
      notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to submit form');
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps = [
    { id: 1, title: 'Basic Information', description: 'Task details and configuration' },
    { id: 2, title: 'Assignment & Points', description: 'Assignees and point distribution' },
    { id: 3, title: 'Advanced Settings', description: 'Approval, documents, and scheduling' }
  ];

  // Prevent form submission on Enter key when not on final step
  const handleFormKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter' && currentStep < totalSteps) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      onKeyDown={handleFormKeyDown}
      className={`${sidePanelStyles.form} ${taskFormStyles.formContainer}`}
    >
      <div className={taskFormStyles.stepIndicator}>
        {steps.map((step, index) => (
          <React.Fragment key={step.id}>
            <div className={taskFormStyles.stepItem}>
              <div className={`${taskFormStyles.stepNumber} ${currentStep > step.id
                ? taskFormStyles.stepNumberCompleted
                : currentStep === step.id
                  ? taskFormStyles.stepNumberActive
                  : taskFormStyles.stepNumberInactive
                }`}>
                {currentStep > step.id ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                ) : (
                  step.id
                )}
              </div>
              <div className={taskFormStyles.stepInfo}>
                <div className={`${taskFormStyles.stepTitle} ${currentStep === step.id
                  ? taskFormStyles.stepTitleActive
                  : taskFormStyles.stepTitleInactive
                  }`}>
                  {step.title}
                </div>

              </div>
            </div>
            {index < steps.length - 1 && (
              <div className={`${taskFormStyles.stepConnector} ${currentStep > step.id
                ? taskFormStyles.stepConnectorActive
                : taskFormStyles.stepConnectorInactive
                }`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Basic Information */}
      {currentStep === 1 && (
        <div className={taskFormStyles.stepContent}>
          {/* Task Name */}
          <div className={sidePanelStyles.formGroup}>
            <label className={sidePanelStyles.formLabel}>
              Task Name <span className="required">*</span>
            </label>
            <input
              type="text"
              className={sidePanelStyles.formControl}
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Task Name"
              required
            />
          </div>

          {/* Description */}
          <div className={sidePanelStyles.formGroup}>
            <label className={sidePanelStyles.formLabel}>
              Description <span className="required">*</span>
            </label>
            <textarea
              className={sidePanelStyles.formControl}
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Task Description"
              rows={3}
              required
            />
          </div>

          {/* Point Scheme Filter - Only show in group context */}
          {showCategoryFilter && onCategoryFilterChange && (
            <div className={sidePanelStyles.formGroup}>
              <label className={sidePanelStyles.formLabel}>
                Point Scheme Filter
              </label>
              <CustomDropdown
                options={[
                  { value: 'all', label: 'All Point Schemes (Global + Group)' },
                  { value: 'global', label: 'Global Point Schemes Only' },
                  { value: 'group', label: 'Group Point Schemes Only' }
                ]}
                value={categoryFilter}
                onChange={(value: string | string[]) => {
                  const filter = (Array.isArray(value) ? value[0] : value) as 'all' | 'global' | 'group';
                  onCategoryFilterChange(filter);
                }}
                placeholder="Select filter"
                size="md"
                variant="outlined"
                className={customDropdownStyles.sizeFull}
              />
            </div>
          )}

          {/* Point Scheme and Priority */}
          <div className={sidePanelStyles.formRow}>
            <div className={sidePanelStyles.formGroup}>
              <label className={sidePanelStyles.formLabel}>
                Point Scheme <span className="required">*</span>
              </label>
              <CustomDropdown
                options={taskCategories.map(cat => ({ value: cat.id, label: cat.name }))}
                value={formData.category_id}
                onChange={(value: string | string[]) => setFormData(prev => ({ ...prev, category_id: Array.isArray(value) ? value[0] : value }))}
                placeholder={isLoadingCategories ? "Loading point schemes..." : taskCategories.length === 0 ? "No point schemes available" : "Select point scheme"}
                size="md"
                variant="outlined"
                className={customDropdownStyles.sizeFull}
                disabled={isLoadingCategories || taskCategories.length === 0}
                searchable={true}
              />

            </div>

            <div className={sidePanelStyles.formGroup}>
              <label className={sidePanelStyles.formLabel}>
                Priority <span className="required">*</span>
              </label>
              <CustomDropdown
                options={priorityOptions}
                value={formData.priority}
                onChange={(value: string | string[]) => setFormData(prev => ({ ...prev, priority: Array.isArray(value) ? value[0] : value }))}
                placeholder="Select priority"
                size="md"
                variant="outlined"
                className={customDropdownStyles.sizeFull}
              />
            </div>
          </div>

          {/* Due Date and Estimated Hours */}
          <div className={sidePanelStyles.formRow}>
            <div className={sidePanelStyles.formGroup}>
              <label className={sidePanelStyles.formLabel}>
                Due Date
              </label>
              <input
                type="date"
                className={sidePanelStyles.formControl}
                value={formData.due_date || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                min={new Date().toISOString().split('T')[0]}
              />

            </div>

            <div className={sidePanelStyles.formGroup}>
              <label className={sidePanelStyles.formLabel}>
                Estimated Hours
              </label>
              <input
                type="number"
                className={sidePanelStyles.formControl}
                value={formData.estimated_hours || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, estimated_hours: parseFloat(e.target.value) || 0 }))}
                min="0"
                step="0.5"
                placeholder="0"
              />

            </div>
          </div>

          {/* Status (only for edit mode and non-template tasks) */}
          {mode === 'edit' && !formData.allow_user_instances && (
            <div className={sidePanelStyles.formGroup}>
              <label className={sidePanelStyles.formLabel}>Status</label>
              <CustomDropdown
                options={statusOptions}
                value={formData.status || 'pending'}
                onChange={(value: string | string[]) => setFormData(prev => ({ ...prev, status: Array.isArray(value) ? value[0] : value }))}
                placeholder="Select status"
                size="md"
                variant="outlined"
                className={customDropdownStyles.sizeFull}
              />
            </div>
          )}

          {/* User Instance Creation Feature - Moved to Basic Information */}
          <div className={sidePanelStyles.formGroup}>
            <label className={`${sidePanelStyles.formLabel} ${taskFormStyles.sectionLabel}`}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={taskFormStyles.sectionLabelIcon}>
                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="8.5" cy="7" r="4"></circle>
                <line x1="20" y1="8" x2="20" y2="14"></line>
                <line x1="23" y1="11" x2="17" y2="11"></line>
              </svg>
              User Instance Creation
            </label>
            <div className={sidePanelStyles.toggleContainer} style={{ marginBottom: '1rem' }}>
              <div className={sidePanelStyles.toggleLabel}>
                <div className={sidePanelStyles.toggleTitle}>Allow users to create their initiatives</div>
                <div className={sidePanelStyles.toggleDescription}>
                  Enable this to create a template that users can use to create their own task instances. Users will be able to create instances based on this template with the configured point allocation rules.
                  {formData.is_recurring && (
                    <span style={{ color: 'var(--warning)', display: 'block', marginTop: '0.25rem' }}>
                      Disable "Enable recurring task" to enable user instances
                    </span>
                  )}
                  {mode === 'edit' && initialData?.allow_user_instances && (
                    <span style={{ color: 'var(--info)', display: 'block', marginTop: '0.25rem' }}>
                      This task was created as a template and cannot be converted to a regular task.
                    </span>
                  )}
                </div>
              </div>
              <label className={sidePanelStyles.toggleSwitch}>
                <input
                  type="checkbox"
                  checked={formData.allow_user_instances}
                  disabled={formData.is_recurring || (mode === 'edit' && initialData?.allow_user_instances)}
                  onChange={(e) => {
                    // Prevent disabling if this is an existing template task
                    if (mode === 'edit' && initialData?.allow_user_instances && !e.target.checked) {
                      notificationService.error('Cannot convert a template task to a regular task. Template tasks must remain as templates.');
                      return;
                    }
                    if (formData.is_recurring && e.target.checked) {
                      notificationService.error('Cannot enable user instances when "Enable recurring task" is enabled. Please disable recurring task first.');
                      return;
                    }
                    setFormData(prev => ({
                      ...prev,
                      allow_user_instances: e.target.checked,
                      is_recurring: e.target.checked ? false : prev.is_recurring,
                      recurrence_type: e.target.checked ? 'none' : prev.recurrence_type
                    }));
                  }}
                />
                <span className={sidePanelStyles.toggleSlider}></span>
              </label>
            </div>
            {formData.allow_user_instances && (
              <div className={taskFormStyles.userInstanceContainer}>
                <div className={sidePanelStyles.formHelp} style={{ marginBottom: '1rem' }}>
                  <i className="bi bi-lightbulb" style={{ color: 'var(--warning)', marginRight: '0.3rem' }}></i>
                  <strong>Template Task:</strong> This will be created as a template. Selected users will be able to create their own instances from this template. Point allocation rules will apply to instances they create.
                </div>

                {/* Deadline Restriction Notice */}
                {formData.due_date && (
                  <div style={{
                    marginBottom: '1rem',
                    padding: '0.75rem',
                    backgroundColor: 'var(--info-lighter)',
                    borderRadius: '8px',
                    border: '1px solid var(--info)',
                    fontSize: '0.875rem',
                    color: 'var(--info-dark)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: '0.125rem', flexShrink: 0 }}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="16" x2="12" y2="12"></line>
                        <line x1="12" y1="8" x2="12.01" y2="8"></line>
                      </svg>
                      <div>
                        <strong>Deadline Restriction:</strong> Users can only create instances of this task up to the configured deadline date ({new Date(formData.due_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}).
                        After this date, instance creation will no longer be available.
                      </div>
                    </div>
                  </div>
                )}

                <div className={sidePanelStyles.formGroup}>
                  <label className={sidePanelStyles.formLabel}>Template Name (for user instances)</label>
                  <input
                    type="text"
                    className={sidePanelStyles.formControl}
                    value={formData.instance_template_name || formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, instance_template_name: e.target.value }))}
                    placeholder="Template Name"
                    style={{ border: '1px solid var(--primary)' }}
                  />
                  <div className={sidePanelStyles.formHelp}>
                    Users will see this name when creating their own instances. Leave empty to use task name.
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Step 2: Assignment & Points */}
      {currentStep === 2 && (
        <div className={taskFormStyles.stepContent}>
          {/* Assign To */}
          <div className={sidePanelStyles.formGroup}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <label className={sidePanelStyles.formLabel}>
                {formData.allow_user_instances ? 'Who Can Create Instances' : 'Assign To'} <span className="required">*</span>
              </label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  type="button"
                  onClick={selectAllAssignees}
                  className={`${buttonStyles.btn} ${buttonStyles.btnSecondary}`}
                  style={{
                    padding: '0.375rem 0.75rem',
                    fontSize: '0.875rem',
                    opacity: formData.assignee_ids.length === groupMembers.length ? 0.6 : 1
                  }}
                  disabled={formData.assignee_ids.length === groupMembers.length}
                  title="Select all group members"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '0.25rem' }}>
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                  Select All
                </button>
                {formData.assignee_ids.length > 0 && (
                  <button
                    type="button"
                    onClick={clearAllAssignees}
                    className={`${buttonStyles.btn} ${buttonStyles.btnSecondary}`}
                    style={{
                      padding: '0.375rem 0.75rem',
                      fontSize: '0.875rem'
                    }}
                    title="Clear all selected assignees"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '0.25rem' }}>
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                    Clear All
                  </button>
                )}
              </div>
            </div>
            <CustomDropdown
              options={getAvailableAssignees().map(member => ({
                value: member.user?.id || member.id,
                label: `${member.user?.first_name || ''} ${member.user?.last_name || ''} (${member.user?.email || member.email || ''})`
              }))}
              value=""
              onChange={(value: string | string[]) => {
                const selectedValue = Array.isArray(value) ? value[0] : value;
                if (selectedValue) {
                  addAssignee(selectedValue);
                }
              }}
              placeholder={
                getAvailableAssignees().length === 0
                  ? (groupMembers.length === 0 && localAssignees.length === 0)
                    ? "No members available to assign"
                    : "All members are already selected"
                  : formData.allow_user_instances
                    ? "Select users who can create instances"
                    : "Select assignees"
              }
              disabled={getAvailableAssignees().length === 0}
              size="md"
              variant="outlined"
              className={customDropdownStyles.sizeFull}
              searchable={true}
            />
            {getAvailableAssignees().length === 0 && formData.assignee_ids.length > 0 && (
              <div className={sidePanelStyles.formHelp} style={{ marginTop: '0.5rem', color: 'var(--info)' }}>
                <i className="bi bi-info-circle" style={{ marginRight: '0.3rem' }}></i>
                All members are selected. Click the X button on any member below to remove them.
              </div>
            )}
            {formData.allow_user_instances && getAvailableAssignees().length > 0 && (
              <div className={sidePanelStyles.formHelp} style={{ marginTop: '0.5rem' }}>
                <i className="bi bi-info-circle" style={{ color: 'var(--info)', marginRight: '0.3rem' }}></i>
                Selected users will be able to create their own instances from this template. Point allocation rules will apply to instances they create.
              </div>
            )}
            {/* Selected Assignees Display */}
            {formData.assignee_ids.length > 0 && (
              <div className={styles.selectedAssignees}>
                {getSelectedAssignees().map(assignee => {
                  const userId = assignee.user?.id || assignee.id;
                  const firstName = assignee.user?.first_name || '';
                  const lastName = assignee.user?.last_name || '';
                  const name = `${firstName} ${lastName}`.trim() || 'Unknown';
                  return (
                    <div key={userId} className={styles.selectedAssignee}>
                      <div className={styles.assigneeAvatar}>
                        {(firstName?.[0] || '') + (lastName?.[0] || '')}
                      </div>
                      <span>{name}</span>
                      <button
                        type="button"
                        className={styles.removeBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          removeAssignee(userId);
                        }}
                        title="Remove assignee"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Point Distribution Method - Only show when multiple assignees are selected */}
          {formData.assignee_ids.length > 1 && (
            <div className={sidePanelStyles.formGroup}>
              <label className={sidePanelStyles.formLabel}>
                Point Distribution Method <span className="required">*</span>
              </label>

              <div className={styles.distributionOptionsInfo}>
                <div className={styles.infoCard}>

                  <div className={styles.infoContent}>
                    <div
                      className={`${styles.distributionMethodItem} ${formData.distribution_method === 'equal' ? styles.selected : ''}`}
                      onClick={() => setFormData(prev => ({ ...prev, distribution_method: 'equal' }))}
                    >
                      <div className={styles.methodIcon}>
                        <i className="bi bi-people"></i>
                      </div>
                      <div className={styles.methodDetails}>
                        <strong>Split Points (Equal Share)</strong>
                        <p>Divide points equally among all selected assignees</p>
                        <span className={styles.example}>80 pts ÷ 2 people = 40 pts each</span>
                      </div>
                    </div>

                    <div
                      className={`${styles.distributionMethodItem} ${formData.distribution_method === 'direct' ? styles.selected : ''}`}
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        distribution_method: 'direct',
                        direct_assignment_type: prev.direct_assignment_type || 'single'
                      }))}
                    >
                      <div className={styles.methodIcon}>
                        <i className="bi bi-person-check"></i>
                      </div>
                      <div className={styles.methodDetails}>
                        <strong>Direct Assignment</strong>
                        <p>
                          {formData.allow_user_instances
                            ? "Each user who creates an instance gets full base points"
                            : "Assign base points directly to every person (no splitting)"}
                        </p>
                        <span className={styles.example}>
                          {formData.allow_user_instances
                            ? "Each instance creator gets full 80 pts"
                            : "Each person gets full 80 pts"}
                        </span>
                      </div>
                    </div>

                    <div
                      className={`${styles.distributionMethodItem} ${formData.distribution_method === 'percentage' ? styles.selected : ''}`}
                      onClick={() => setFormData(prev => ({ ...prev, distribution_method: 'percentage' }))}
                    >
                      <div className={styles.methodIcon}>
                        <i className="bi bi-percent"></i>
                      </div>
                      <div className={styles.methodDetails}>
                        <strong>Percentage-based</strong>
                        <p>Distribute based on work contribution percentage</p>
                        <span className={styles.example}>Person A: 60%, Person B: 40% = 48 pts, 32 pts</span>
                      </div>
                    </div>

                    <div
                      className={`${styles.distributionMethodItem} ${formData.distribution_method === 'custom' ? styles.selected : ''}`}
                      onClick={() => setFormData(prev => ({ ...prev, distribution_method: 'custom' }))}
                    >
                      <div className={styles.methodIcon}>
                        <i className="bi bi-gear"></i>
                      </div>
                      <div className={styles.methodDetails}>
                        <strong>Custom Allocation</strong>
                        <p>Manually assign specific points to each person</p>
                        <span className={styles.example}>Person A: 50 pts, Person B: 30 pts</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* Point Allocation - Only show when multiple assignees and distribution method requires allocation */}
          {formData.assignee_ids.length > 1 && formData.distribution_method !== 'equal' && formData.distribution_method !== 'direct' && (
            <div className={sidePanelStyles.formGroup}>
              <label className={sidePanelStyles.formLabel}>Point Allocation</label>
              <div className={styles.pointDistributionDetails}>
                {formData.distribution_method === 'percentage' && (
                  <div className={sidePanelStyles.formHelp}>
                    <i className="bi bi-info-circle" style={{ color: 'var(--info)', marginRight: '0.3rem' }}></i>
                    <strong>Percentage Distribution:</strong> Enter the percentage of work contribution for each assignee. Total must equal 100%.
                  </div>
                )}

                {formData.distribution_method === 'custom' && (
                  <div className={sidePanelStyles.formHelp}>
                    <i className="bi bi-info-circle" style={{ color: 'var(--info)', marginRight: '0.3rem' }}></i>
                    <strong>Custom Allocation:</strong> Manually assign specific points to each person. Total cannot exceed {getRemainingPoints()} points ({getBasePoints()} base - {getTeamLeadPoints()} team lead points).
                  </div>
                )}

                <div className={styles.assigneePointAllocation}>
                  {getSelectedAssignees().map(assignee => {
                    const userId = assignee.user?.id || assignee.id;
                    const firstName = assignee.user?.first_name || '';
                    const lastName = assignee.user?.last_name || '';
                    const name = `${firstName} ${lastName}`.trim() || 'Unknown';
                    const initials = name.split(' ').map(n => n[0]).join('').toUpperCase();

                    return (
                      <div key={userId} className={styles.assigneePointItem}>
                        <div className={styles.assigneeInfo}>
                          <div className={styles.assigneeAvatarSmall}>
                            {initials}
                          </div>
                          <span className={styles.assigneeName}>{name}</span>
                        </div>

                        {(formData.distribution_method === 'percentage' || formData.distribution_method === 'custom') && (
                          <div className={styles.pointInputContainer}>
                            <input
                              type="number"
                              className={styles.pointInput}
                              value={formData.point_allocation[userId] || ''}
                              onChange={(e) => updatePointAllocation(userId, parseFloat(e.target.value) || 0)}
                              min="0"
                              max={formData.distribution_method === 'percentage' ? 100 : getRemainingPoints()}
                              step={formData.distribution_method === 'percentage' ? 0.1 : 1}
                              placeholder="0"
                            />
                            <span className={styles.inputSuffix}>
                              {formData.distribution_method === 'percentage' ? '%' : 'pts'}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', justifyContent: 'center' }}>


                  <div className={`${styles.validationMessage} ${isPointAllocationValid() ? styles.validationSuccess : styles.validationError}`}>
                    {getPointAllocationMessage().message}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Work Descriptions - Only show for non-template tasks */}
          {formData.assignee_ids.length > 0 && !formData.allow_user_instances && (
            <div className={sidePanelStyles.formGroup}>
              <label className={sidePanelStyles.formLabel}>Work Assignments</label>

              <div className={styles.assigneeWorkAllocation}>
                {getSelectedAssignees().map(assignee => {
                  const userId = assignee.user?.id || assignee.id;
                  const firstName = assignee.user?.first_name || '';
                  const lastName = assignee.user?.last_name || '';
                  const name = `${firstName} ${lastName}`.trim() || 'Unknown';
                  const initials = name.split(' ').map(n => n[0]).join('').toUpperCase();

                  return (
                    <div key={userId} className={styles.assigneeWorkItem}>


                      <div className={styles.workDescriptionContainer}>
                        <label className={styles.workDescriptionLabel}>
                          What should {firstName || 'this person'} work on? <span className="required">*</span>
                        </label>
                        <textarea
                          className={styles.workDescriptionInput}
                          value={formData.work_descriptions[userId] || ''}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            work_descriptions: {
                              ...prev.work_descriptions,
                              [userId]: e.target.value
                            }
                          }))}
                          placeholder={`Describe the specific work/responsibility for ${firstName || 'this person'}...`}
                          rows={3}
                          required
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}



          {/* Direct Assignment Type Selection - Only show when multiple assignees and direct assignment is selected AND NOT a template */}
          {!formData.allow_user_instances && formData.assignee_ids.length > 1 && formData.distribution_method === 'direct' && (
            <div className={sidePanelStyles.formGroup}>
              <label className={sidePanelStyles.formLabel}>
                Task Creation Mode <span className="required">*</span>
              </label>
              <div className={styles.distributionOptionsInfo}>
                <div className={styles.infoCard}>
                  <div className={styles.infoContent}>
                    <div
                      className={`${styles.distributionMethodItem} ${formData.direct_assignment_type === 'instances' ? styles.selected : ''}`}
                      onClick={() => setFormData(prev => ({ ...prev, direct_assignment_type: 'instances' }))}
                    >
                      <div className={styles.methodIcon}>
                        <i className="bi bi-files"></i>
                      </div>
                      <div className={styles.methodDetails}>
                        <strong>Create as Instances</strong>
                        <p>Create separate task instances for each assignee. Each person gets their own independent task with full base points.</p>
                        <span className={styles.example}>3 assignees = 3 separate tasks (each with {getBasePoints()} points)</span>
                      </div>
                    </div>

                    <div
                      className={`${styles.distributionMethodItem} ${formData.direct_assignment_type === 'single' ? styles.selected : ''}`}
                      onClick={() => setFormData(prev => ({ ...prev, direct_assignment_type: 'single' }))}
                    >
                      <div className={styles.methodIcon}>
                        <i className="bi bi-file-earmark"></i>
                      </div>
                      <div className={styles.methodDetails}>
                        <strong>Combine as Single Task</strong>
                        <p>Create one task shared by all assignees. Each person receives full base points from the same task.</p>
                        <span className={styles.example}>3 assignees = 1 shared task (each gets {getBasePoints()} points)</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Point Configuration Display */}
          <div className={sidePanelStyles.formGroup}>
            <label className={sidePanelStyles.formLabel}>Point Configuration</label>
            <div className={styles.pointConfigDisplay}>
              <div className={styles.pointInfo}>
                <span className={styles.pointLabel}>Base Points:</span>
                <span className={styles.pointValue}>{getBasePoints() || '-'}</span>
              </div>
              <div className={styles.pointInfo}>
                <span className={styles.pointLabel}>Team Lead Points:</span>
                <span className={styles.pointValue}>{getTeamLeadPoints() || '-'}</span>
              </div>
              {formData.assignee_ids.length > 1 ? (
                <>
                  <div className={styles.pointInfo}>
                    <span className={styles.pointLabel}>Distribution Method:</span>
                    <span className={styles.pointValue}>
                      {formData.distribution_method === 'equal' ? 'Equal Share' :
                        formData.distribution_method === 'direct' ? 'Direct Assignment' :
                          formData.distribution_method === 'percentage' ? 'Percentage-based' :
                            'Custom Allocation'}
                    </span>
                  </div>
                  {formData.distribution_method === 'direct' && formData.direct_assignment_type && (
                    <div className={styles.pointInfo}>
                      <span className={styles.pointLabel}>Task Creation:</span>
                      <span className={styles.pointValue}>
                        {formData.direct_assignment_type === 'instances' ? 'Create as Separate Instances' : 'Combine as Single Task'}
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <div className={styles.pointInfo}>
                  <span className={styles.pointLabel}>Assignment:</span>
                  <span className={styles.pointValue}>All points will be assigned to the selected assignee</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Advanced Settings */}
      {currentStep === 3 && (
        <div className={taskFormStyles.stepContent}>
          {/* Approval Type */}
          <div className={sidePanelStyles.formGroup}>
            <label className={sidePanelStyles.formLabel}>
              Approval Type <span className="required">*</span>
            </label>
            <CustomDropdown
              options={[
                { value: 'none', label: 'No Approval Required' },
                { value: 'single', label: 'Single Approval' },
                { value: 'multiple', label: 'Multiple Approvals' }
              ]}
              value={formData.approval_type}
              onChange={(value: string | string[]) => {
                const approvalValue = (Array.isArray(value) ? value[0] : value) as 'none' | 'single' | 'multiple';
                setFormData(prev => ({
                  ...prev,
                  approval_type: approvalValue,
                  approver_ids: approvalValue !== 'multiple' ? [] : prev.approver_ids,
                  requires_team_lead_approval: approvalValue === 'single' ? true : (approvalValue === 'none' ? false : prev.requires_team_lead_approval)
                }));
                // Clear approver data when switching away from multiple
                if (approvalValue !== 'multiple') {
                  setSelectedApproversData({});
                }
              }}
              placeholder="Select approval type"
              size="md"
              variant="outlined"
              className={customDropdownStyles.sizeFull}
            />
            {formData.approval_type === 'multiple' && (
              <small className={sidePanelStyles.formText} style={{ marginTop: '0.5rem', display: 'block' }}>
                {formData.approver_ids && formData.approver_ids.length > 0
                  ? `${formData.approver_ids.length} approver${formData.approver_ids.length > 1 ? 's' : ''} selected. All must approve to complete the task.`
                  : 'Select approvers who need to approve this task. All selected approvers must approve to mark the task as completed.'}
              </small>
            )}
          </div>

          {/* Team Lead Approval Checkbox */}
          {formData.approval_type !== 'none' && (
            <div className={sidePanelStyles.toggleContainer} style={{ opacity: formData.approval_type === 'single' ? 0.8 : 1 }}>
              <div
                className={sidePanelStyles.toggleLabel}
                onClick={() => {
                  if (formData.approval_type === 'single') return;
                  setFormData(prev => ({ ...prev, requires_team_lead_approval: !prev.requires_team_lead_approval }));
                }}
                style={{ cursor: formData.approval_type === 'single' ? 'not-allowed' : 'pointer' }}
              >
                <div className={sidePanelStyles.toggleTitle}>Require Team Lead Approval</div>
                <div className={sidePanelStyles.toggleDescription}>Additional approval from group team lead</div>
              </div>
              <label className={sidePanelStyles.toggleSwitch}>
                <input
                  type="checkbox"
                  checked={formData.requires_team_lead_approval}
                  disabled={formData.approval_type === 'single'}
                  onChange={(e) => setFormData(prev => ({ ...prev, requires_team_lead_approval: e.target.checked }))}
                />
                <span className={sidePanelStyles.toggleSlider}></span>
              </label>
            </div>
          )}

          {/* Approver Selection (for Multiple Approvals) */}
          {formData.approval_type === 'multiple' && (
            <div className={sidePanelStyles.formGroup}>
              <label className={sidePanelStyles.formLabel}>
                Select Approvers <span className="required">*</span>
              </label>

              {/* Approver Dropdown with Search */}
              <div ref={approverDropdownRef} style={{ position: 'relative' }}>
                <div
                  onClick={() => {
                    setApproverDropdownOpen(!approverDropdownOpen);
                  }}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    minHeight: '44px'
                  }}
                >
                  <span style={{
                    color: (approverSearchQuery || formData.approver_ids?.length) ? 'var(--dark)' : 'var(--gray)',
                    fontSize: '0.875rem'
                  }}>
                    {approverSearchQuery
                      ? approverSearchQuery
                      : formData.approver_ids && formData.approver_ids.length > 0
                        ? `${formData.approver_ids.length} approver${formData.approver_ids.length > 1 ? 's' : ''} selected`
                        : 'Click to search and select approvers...'}
                  </span>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{
                      transform: approverDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s'
                    }}
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </div>

                {/* Dropdown Menu */}
                {approverDropdownOpen && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    zIndex: 1000,
                    marginTop: '4px',
                    maxHeight: '400px',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    {/* Search Input */}
                    <div style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-light)' }}>
                      <input
                        type="text"
                        placeholder="Search by name, email, designation, or ID..."
                        value={approverSearchQuery}
                        onChange={(e) => {
                          const query = e.target.value;
                          setApproverSearchQuery(query);

                          // Clear previous timeout
                          if (approverSearchTimeoutRef.current) {
                            clearTimeout(approverSearchTimeoutRef.current);
                          }

                          // Debounce search
                          approverSearchTimeoutRef.current = setTimeout(() => {
                            const trimmedQuery = query.trim();
                            if (trimmedQuery.length >= 1) {
                              loadApprovers(trimmedQuery);
                            } else if (trimmedQuery.length === 0 && allApprovers.length > 0) {
                              // If cleared, reload initial set
                              loadApprovers('a');
                            }
                          }, 300);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid var(--border)',
                          borderRadius: '6px',
                          fontSize: '0.875rem'
                        }}
                        autoFocus
                      />
                    </div>

                    {/* Dropdown Options */}
                    <div style={{
                      maxHeight: '300px',
                      overflowY: 'auto'
                    }}>
                      {approverSearchLoading ? (
                        <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--gray)' }}>
                          <span>Loading...</span>
                        </div>
                      ) : getFilteredApprovers().length > 0 ? (
                        getFilteredApprovers().map((user: any) => {
                          const initials = `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase();
                          const displayName = getUserDisplayName(user);
                          return (
                            <div
                              key={user.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                addApprover(user);
                              }}
                              style={{
                                padding: '0.75rem 1rem',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                                borderBottom: '1px solid var(--border-light)',
                                transition: 'background-color 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'var(--gray-lighter)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              <div style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '50%',
                                backgroundColor: 'var(--primary)',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.875rem',
                                fontWeight: '600',
                                flexShrink: 0
                              }}>
                                {initials}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: '600', color: 'var(--dark)', marginBottom: '0.25rem' }}>
                                  {displayName}
                                </div>
                                <div style={{ fontSize: '0.875rem', color: 'var(--gray)' }}>
                                  {user.email}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--gray)' }}>
                          {approverSearchLoading
                            ? 'Searching...'
                            : approverSearchQuery.trim().length >= 2
                              ? 'No users found'
                              : approverSearchQuery.trim().length === 1
                                ? 'Type at least 2 characters to search...'
                                : 'Start typing to search for users...'}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Selected Approvers List */}
              {formData.approver_ids && formData.approver_ids.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '0.5rem',
                    marginTop: '0.5rem'
                  }}>
                    {/* Display selected approvers */}
                    {formData.approver_ids.map((approverId: string) => {
                      // Normalize ID to string for consistent lookup
                      const approverIdStr = String(approverId);
                      const approver = selectedApproversData[approverIdStr];
                      const initials = approver
                        ? `${approver.first_name?.[0] || ''}${approver.last_name?.[0] || ''}`.toUpperCase()
                        : approverIdStr.slice(0, 2).toUpperCase();
                      const displayName = approver ? getUserDisplayName(approver) : `User (${approverIdStr.slice(0, 8)}...)`;
                      return (
                        <div
                          key={approverId}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.5rem 0.75rem',
                            backgroundColor: 'var(--gray-lighter)',
                            borderRadius: '6px',
                            border: '1px solid var(--border)'
                          }}
                        >
                          <div style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '50%',
                            backgroundColor: 'var(--primary)',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            flexShrink: 0
                          }}>
                            {initials}
                          </div>
                          <span style={{ fontSize: '0.875rem', color: 'var(--dark)', fontWeight: '500' }}>
                            {displayName}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeApprover(approverId)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '0',
                              display: 'flex',
                              alignItems: 'center',
                              color: 'var(--danger)'
                            }}
                            title="Remove approver"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="18" y1="6" x2="6" y2="18"></line>
                              <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{
                    marginTop: '0.75rem',
                    padding: '0.75rem',
                    backgroundColor: 'var(--info-lighter)',
                    borderRadius: '6px',
                    border: '1px solid var(--info)',
                    fontSize: '0.875rem',
                    color: 'var(--info-dark)'
                  }}>
                    <strong>Note:</strong> All {formData.approver_ids.length} selected approver{formData.approver_ids.length > 1 ? 's' : ''} must approve for the task to be marked as completed.
                  </div>
                </div>
              )}

              {(!formData.approver_ids || formData.approver_ids.length === 0) && (
                <div style={{
                  marginTop: '0.5rem',
                  padding: '0.75rem',
                  backgroundColor: 'var(--warning-lighter)',
                  borderRadius: '6px',
                  border: '1px solid var(--warning)',
                  fontSize: '0.875rem',
                  color: 'var(--warning-dark)'
                }}>
                  Please select at least one approver for multiple approvals.
                </div>
              )}
            </div>
          )}



          {/* Require Documents Upload */}
          <div className={sidePanelStyles.formGroup}>
            <div className={sidePanelStyles.toggleContainer}>
              <div className={sidePanelStyles.toggleLabel}>
                <div className={sidePanelStyles.toggleTitle}>Require documents upload</div>
                <div className={sidePanelStyles.toggleDescription}>Users must upload documents to complete this task</div>
              </div>
              <label className={sidePanelStyles.toggleSwitch}>
                <input
                  type="checkbox"
                  checked={formData.requires_documents}
                  onChange={(e) => setFormData(prev => ({ ...prev, requires_documents: e.target.checked }))}
                />
                <span className={sidePanelStyles.toggleSlider}></span>
              </label>
            </div>
          </div>

          {/* Scheduler/Recurrence - HIDDEN TEMPORARILY */}
          {false && (
            <div className={sidePanelStyles.formGroup}>
              <label className={`${sidePanelStyles.formLabel} ${taskFormStyles.sectionLabel}`}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={taskFormStyles.sectionLabelIcon}>
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                Task Scheduler / Recurrence
              </label>
              <div className={sidePanelStyles.toggleContainer} style={{ marginBottom: '1rem' }}>
                <div className={sidePanelStyles.toggleLabel}>
                  <div className={sidePanelStyles.toggleTitle}>Enable recurring task</div>
                  <div className={sidePanelStyles.toggleDescription}>
                    Automatically repeat this task based on schedule (daily, weekly, monthly, or yearly)
                    {formData.allow_user_instances && (
                      <span style={{ color: 'var(--warning)', display: 'block', marginTop: '0.25rem' }}>
                        Disable "Allow users to create their initiatives" to enable recurrence
                      </span>
                    )}
                  </div>
                </div>
                <label className={sidePanelStyles.toggleSwitch}>
                  <input
                    type="checkbox"
                    checked={formData.is_recurring}
                    disabled={formData.allow_user_instances}
                    onChange={(e) => {
                      if (formData.allow_user_instances && e.target.checked) {
                        notificationService.error('Cannot enable recurring task when "Allow users to create their initiatives" is enabled. Please disable user instances first.');
                        return;
                      }
                      setFormData(prev => ({
                        ...prev,
                        is_recurring: e.target.checked,
                        recurrence_type: e.target.checked ? prev.recurrence_type : 'none',
                        allow_user_instances: e.target.checked ? false : prev.allow_user_instances
                      }));
                    }}
                  />
                  <span className={sidePanelStyles.toggleSlider}></span>
                </label>
              </div>

              {formData.is_recurring && (
                <div className={taskFormStyles.recurrenceContainer}>
                  <div className={sidePanelStyles.formGroup}>
                    <label className={sidePanelStyles.formLabel}>Repeat Frequency</label>
                    <CustomDropdown
                      options={[
                        { value: 'daily', label: 'Daily' },
                        { value: 'weekly', label: 'Weekly (select days)' },
                        { value: 'monthly', label: 'Monthly (select dates)' },
                        { value: 'yearly', label: 'Yearly' }
                      ]}
                      value={formData.recurrence_type}
                      onChange={(value: string | string[]) => setFormData(prev => ({
                        ...prev,
                        recurrence_type: (Array.isArray(value) ? value[0] : value) as 'daily' | 'weekly' | 'monthly' | 'yearly'
                      }))}
                      placeholder="Select frequency"
                      size="md"
                      variant="outlined"
                      className={customDropdownStyles.sizeFull}
                    />
                  </div>

                  {/* Weekly - Day Selection */}
                  {formData.recurrence_type === 'weekly' && (
                    <div className={sidePanelStyles.formGroup}>
                      <label className={sidePanelStyles.formLabel}>Repeat on Days</label>
                      <div className={taskFormStyles.daySelectionContainer}>
                        {[
                          { value: 0, label: 'Sun' },
                          { value: 1, label: 'Mon' },
                          { value: 2, label: 'Tue' },
                          { value: 3, label: 'Wed' },
                          { value: 4, label: 'Thu' },
                          { value: 5, label: 'Fri' },
                          { value: 6, label: 'Sat' }
                        ].map(day => (
                          <button
                            key={day.value}
                            type="button"
                            onClick={() => {
                              const currentDays = formData.recurrence_weekly_days || [];
                              const newDays = currentDays.includes(day.value)
                                ? currentDays.filter(d => d !== day.value)
                                : [...currentDays, day.value];
                              setFormData(prev => ({ ...prev, recurrence_weekly_days: newDays }));
                            }}
                            className={`${taskFormStyles.dayButton} ${formData.recurrence_weekly_days?.includes(day.value)
                              ? taskFormStyles.dayButtonSelected
                              : ''
                              }`}
                          >
                            {day.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Monthly - Date Selection */}
                  {formData.recurrence_type === 'monthly' && (
                    <div className={sidePanelStyles.formGroup}>
                      <label className={sidePanelStyles.formLabel}>Repeat on Dates</label>
                      <div className={taskFormStyles.dateSelectionContainer}>
                        {[1, 5, 10, 15, 20, 25, 28].map(date => (
                          <button
                            key={date}
                            type="button"
                            onClick={() => {
                              const currentDates = formData.recurrence_monthly_dates || [];
                              const newDates = currentDates.includes(date)
                                ? currentDates.filter(d => d !== date)
                                : [...currentDates, date];
                              setFormData(prev => ({ ...prev, recurrence_monthly_dates: newDates }));
                            }}
                            className={`${taskFormStyles.dateButton} ${formData.recurrence_monthly_dates?.includes(date)
                              ? taskFormStyles.dateButtonSelected
                              : ''
                              }`}
                          >
                            {date}{date === 1 ? 'st' : date === 2 ? 'nd' : date === 3 ? 'rd' : 'th'}
                          </button>
                        ))}
                      </div>
                      <input
                        type="text"
                        className={sidePanelStyles.formControl}
                        placeholder="Or enter custom dates (comma-separated, e.g., 1,15,30)"
                        value={formData.recurrence_monthly_dates?.join(',') || ''}
                        onChange={(e) => {
                          const dates = e.target.value.split(',').map(d => parseInt(d.trim())).filter(d => !isNaN(d) && d >= 1 && d <= 31);
                          setFormData(prev => ({ ...prev, recurrence_monthly_dates: dates }));
                        }}
                      />
                    </div>
                  )}

                  {/* Yearly - Date Selection */}
                  {formData.recurrence_type === 'yearly' && (
                    <div className={sidePanelStyles.formRow}>
                      <div className={sidePanelStyles.formGroup}>
                        <label className={sidePanelStyles.formLabel}>Month</label>
                        <CustomDropdown
                          options={[
                            { value: '1', label: 'January' },
                            { value: '2', label: 'February' },
                            { value: '3', label: 'March' },
                            { value: '4', label: 'April' },
                            { value: '5', label: 'May' },
                            { value: '6', label: 'June' },
                            { value: '7', label: 'July' },
                            { value: '8', label: 'August' },
                            { value: '9', label: 'September' },
                            { value: '10', label: 'October' },
                            { value: '11', label: 'November' },
                            { value: '12', label: 'December' }
                          ]}
                          value={formData.recurrence_yearly_date?.month?.toString() || '1'}
                          onChange={(value: string | string[]) => setFormData(prev => ({
                            ...prev,
                            recurrence_yearly_date: {
                              ...prev.recurrence_yearly_date,
                              month: parseInt(Array.isArray(value) ? value[0] : value)
                            }
                          }))}
                          placeholder="Select month"
                          size="md"
                          variant="outlined"
                          className={customDropdownStyles.sizeFull}
                        />
                      </div>
                      <div className={sidePanelStyles.formGroup}>
                        <label className={sidePanelStyles.formLabel}>Day</label>
                        <input
                          type="number"
                          className={sidePanelStyles.formControl}
                          min="1"
                          max="31"
                          value={formData.recurrence_yearly_date?.day || 1}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            recurrence_yearly_date: {
                              ...prev.recurrence_yearly_date,
                              day: parseInt(e.target.value) || 1
                            }
                          }))}
                        />
                      </div>
                    </div>
                  )}

                  {/* Recurrence End Date (Optional) */}
                  {formData.is_recurring && formData.recurrence_type !== 'none' && (
                    <div className={sidePanelStyles.formGroup}>
                      <label className={sidePanelStyles.formLabel}>
                        End Date <span className={taskFormStyles.optionalLabel}>(Optional)</span>
                      </label>
                      <input
                        type="date"
                        className={sidePanelStyles.formControl}
                        value={formData.recurrence_end_date || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, recurrence_end_date: e.target.value }))}
                      />
                      <div className={sidePanelStyles.formHelp}>
                        Leave empty to repeat indefinitely
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Document Upload Section */}
          <div className={sidePanelStyles.formGroup}>
            <label className={sidePanelStyles.formLabel}>
              Attach Documents
            </label>
            <div
              className={styles.fileUploadArea}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                type="file"
                id="documentUpload"
                multiple
                accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.xlsx,.xls,.ppt,.pptx"
                onChange={handleFileUpload}
                className={styles.fileInput}
              />
              <label htmlFor="documentUpload" className={styles.fileUploadLabel}>
                <div className={styles.fileUploadIcon}>
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <polyline points="7,10 12,15 17,10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <line x1="12" y1="15" x2="12" y2="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div className={styles.fileUploadText}>
                  {isValidatingFiles ? (
                    <>
                      <strong>Validating files for security...</strong>
                      <span>Please wait while we scan your files</span>
                    </>
                  ) : (
                    <>
                      <strong>Drag & drop your files here</strong>
                      <span>
                        or <span className={styles.browseLink}>browse</span> to select files
                      </span>
                    </>
                  )}
                  <div className={styles.acceptedFileTypes}>
                    <div className={styles.fileTypeItem}>
                      <span>{getFileIcon('application/pdf', 'document.pdf')}</span>
                      <span>.pdf</span>
                    </div>
                    <div className={styles.fileTypeItem}>
                      <span>{getFileIcon('application/msword', 'document.doc')}</span>
                      <span>.doc</span>
                    </div>
                    <div className={styles.fileTypeItem}>
                      <span>{getFileIcon('application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'document.docx')}</span>
                      <span>.docx</span>
                    </div>
                    <div className={styles.fileTypeItem}>
                      {getFileIcon('text/plain', 'document.txt')}
                      <span>.txt</span>
                    </div>
                    <div className={styles.fileTypeItem}>
                      <span>{getFileIcon('image/jpeg', 'image.jpg')}</span>
                      <span>.jpg</span>
                    </div>
                    <div className={styles.fileTypeItem}>
                      <span>{getFileIcon('image/png', 'image.png')}</span>
                      <span>.png</span>
                    </div>
                    <div className={styles.fileTypeItem}>
                      <span>{getFileIcon('application/vnd.ms-excel', 'spreadsheet.xlsx')}</span>
                      <span>.xlsx</span>
                    </div>
                  </div>
                  <small>
                    Max 50MB each • Security scanning enabled • Executable files blocked
                  </small>
                </div>
              </label>
            </div>

            {/* Uploaded Files List */}
            {uploadedFiles.length > 0 && (
              <div className={styles.uploadedFiles}>
                <h4 className={styles.uploadedFilesTitle}>Uploaded Files ({uploadedFiles.length})</h4>
                {uploadedFiles.map((file, index) => {
                  const metadata = documentMetadata[Object.keys(documentMetadata)[index]];
                  return (
                    <div key={index} className={styles.uploadedFileItem}>
                      <div className={styles.fileInfo}>
                        <div className={styles.fileIcon}>
                          {getFileIcon(file.type, file.name)}
                        </div>
                        <div className={styles.fileDetails}>
                          <div className={styles.fileName}>{file.name}</div>
                          <div className={styles.fileSize}>{(file.size / 1024 / 1024).toFixed(2)} MB</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className={styles.removeFileBtn}
                          title="Remove file"
                        >
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                      </div>

                      {/* File Metadata */}
                      <div className={styles.fileMetadata}>
                        <div className={sidePanelStyles.formRow}>
                          <div className={sidePanelStyles.formGroup}>
                            <label className={sidePanelStyles.formLabel}>Title</label>
                            <input
                              type="text"
                              className={sidePanelStyles.formControl}
                              value={metadata?.title || ''}
                              onChange={(e) => updateDocumentMetadata(index, 'title', e.target.value)}
                              placeholder="Document title"
                            />
                          </div>
                          <div className={sidePanelStyles.formGroup}>
                            <label className={sidePanelStyles.formLabel}>Type</label>
                            <CustomDropdown
                              options={[
                                { value: 'attachment', label: 'Attachment' },
                                { value: 'guideline', label: 'Guideline' },
                                { value: 'template', label: 'Template' },
                                { value: 'reference', label: 'Reference Material' },
                                { value: 'other', label: 'Other' }
                              ]}
                              value={metadata?.document_type || 'attachment'}
                              onChange={(value) => updateDocumentMetadata(index, 'document_type', Array.isArray(value) ? value[0] : value)}
                              placeholder="Select document type"
                            />
                          </div>
                        </div>

                        <div className={sidePanelStyles.formGroup}>
                          <label className={sidePanelStyles.formLabel}>Description</label>
                          <textarea
                            className={sidePanelStyles.formControl}
                            value={metadata?.description || ''}
                            onChange={(e) => updateDocumentMetadata(index, 'description', e.target.value)}
                            placeholder="Document description (optional)"
                            rows={2}
                          />
                        </div>

                        <div className={sidePanelStyles.formGroup}>
                          <div className={sidePanelStyles.formCheck}>
                            <input
                              type="checkbox"
                              id={`required-${index}`}
                              className={sidePanelStyles.formCheckInput}
                              checked={metadata?.is_required || false}
                              onChange={(e) => updateDocumentMetadata(index, 'is_required', e.target.checked)}
                            />
                            <label htmlFor={`required-${index}`} className={sidePanelStyles.formCheckLabel}>
                              Required for task completion
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Existing Documents (for edit mode) */}
          {mode === 'edit' && existingDocuments && existingDocuments.length > 0 && (
            <div className={sidePanelStyles.formGroup}>
              <label className={sidePanelStyles.formLabel}>
                Existing Documents ({existingDocuments.length})
              </label>
              <div className={styles.existingDocuments}>
                {existingDocuments.map((doc) => (
                  <div key={doc.id} className={styles.documentItem}>
                    <div className={styles.documentInfo}>
                      <div className={styles.documentIcon}>
                        {getFileIcon(doc.file_type, doc.filename)}
                      </div>
                      <div className={styles.documentDetails}>
                        <div className={styles.documentName}>{doc.title}</div>
                        <div className={styles.documentMeta}>
                          {doc.filename} • {doc.file_size_display} • {doc.document_type}
                          {doc.is_required && <span className={styles.requiredBadge}>Required</span>}
                        </div>
                      </div>
                      <div className={styles.documentActions}>
                        {onViewDocument && (
                          <button
                            type="button"
                            onClick={() => onViewDocument(doc)}
                            className={styles.viewBtn}
                            title="View document"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                          </button>
                        )}
                        {onDownloadDocument && (
                          <button
                            type="button"
                            onClick={() => onDownloadDocument(doc)}
                            className={styles.downloadBtn}
                            title="Download document"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                              <polyline points="7,10 12,15 17,10" />
                              <line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                          </button>
                        )}
                        {onDeleteDocument && (
                          <button
                            type="button"
                            onClick={() => onDeleteDocument(doc.id)}
                            className={styles.deleteBtn}
                            title="Delete document"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3,6 5,6 21,6" />
                              <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                    {doc.description && (
                      <div className={styles.documentDescription}>
                        {doc.description}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step Navigation */}
      <div className={`${sidePanelStyles.formActions} ${taskFormStyles.stepNavigation}`}>
        <div className={taskFormStyles.navigationLeft}>
          {currentStep > 1 && (
            <button
              type="button"
              className={`${buttonStyles.btn} ${buttonStyles.btnSecondary} ${taskFormStyles.navButton}`}
              onClick={(e) => handleBack(e)}
              disabled={isSubmitting}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={taskFormStyles.navButtonIcon}>
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              Back
            </button>
          )}
          <button
            type="button"
            className={`${buttonStyles.btn} ${buttonStyles.btnSecondary}`}
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </button>
        </div>
        <div className={taskFormStyles.navigationRight}>
          {currentStep < totalSteps ? (
            <button
              type="button"
              className={`${buttonStyles.btn} ${buttonStyles.btnPrimary} ${taskFormStyles.navButton}`}
              onClick={(e) => handleNext(e)}
              disabled={isSubmitting}
            >
              Next
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={taskFormStyles.navButtonIcon}>
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
          ) : (
            <button
              type="submit"
              className={`${buttonStyles.btn} ${buttonStyles.btnPrimary} ${taskFormStyles.navButton}`}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <svg className={`${styles.spinner} ${taskFormStyles.navButtonIcon}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                  {mode === 'edit' ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  {mode === 'edit' ? 'Update Task' : 'Create Task'}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={taskFormStyles.navButtonIcon}>
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </form>
  );
};

export default TaskForm;

